import React, { Component, ErrorInfo, ReactNode } from "react";

const CHUNK_REFRESH_KEY = "chunk_error_auto_refresh";

function isChunkLoadError(error: Error): boolean {
  const msg = (error?.message || "").toLowerCase();
  const name = (error?.name || "").toLowerCase();
  return (
    name === "chunkloaderror" ||
    msg.includes("loading chunk") ||
    msg.includes("chunk load failed") ||
    msg.includes("dynamic import") ||
    msg.includes("loading css chunk")
  );
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * 捕获 ChunkLoadError（如 chunk 404），首次自动刷新一次，避免部署后旧缓存导致白屏
 */
export class ChunkLoadErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, _errorInfo: ErrorInfo): void {
    if (!isChunkLoadError(error)) return;
    const alreadyRefreshed = sessionStorage.getItem(CHUNK_REFRESH_KEY);
    if (!alreadyRefreshed) {
      sessionStorage.setItem(CHUNK_REFRESH_KEY, "1");
      window.location.reload();
    }
  }

  override render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      if (isChunkLoadError(this.state.error)) {
        return (
          this.props.fallback ?? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "200px",
                padding: "24px",
                textAlign: "center",
              }}
            >
              <p style={{ marginBottom: "16px", color: "#666" }}>
                加载失败，可能是网络或缓存问题
              </p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                style={{
                  padding: "8px 20px",
                  fontSize: "14px",
                  cursor: "pointer",
                  background: "#1a73e8",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                }}
              >
                刷新页面
              </button>
            </div>
          )
        );
      }
      throw this.state.error;
    }
    return this.props.children;
  }
}
